import { Link } from "react-router-dom";

import {
  NvCheckCircleIcon,
  NvCodeIcon,
  NvFolderIcon,
  NvKeyIcon,
  NvPageIcon,
  NvSendIcon,
} from "@/components/navigator/nv-icons-internal";
import { cn } from "@/lib/utils";
import type { InternalComparison, InternalProject } from "@/services/navigator/internal-detail-types";

import { SupplierLogo } from "./supplier-logo";
import { InternalSection, PictoTile } from "./internal-parts";

// 프로젝트 정보(PwC 내부만) · 유사 에이전트 비교 (Figma ver.7 03-09~12)

function Person({ name, role }: { name: string; role: string }) {
  return (
    <div className="flex h-[107px] items-center gap-[14px] rounded-[12px] border border-nv-border bg-white px-[21px]">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-nv-surface-inverse text-[16px] font-medium leading-[19px] text-white">{name.slice(0, 1)}</span>
      <span className="flex min-w-0 flex-col">
        <span className="text-[16px] font-medium leading-6 text-nv-text-primary">{name}</span>
        <span className="text-[13px] leading-5 text-nv-text-secondary">{role}</span>
      </span>
    </div>
  );
}

function DevTile({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <li className="flex min-h-[156px] flex-col rounded-[12px] bg-nv-bg-page px-5 py-[18px]">
      <span className="flex items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-white text-nv-brand">{icon}</span>
        <span className="text-[13px] leading-5 text-nv-text-tertiary">{label}</span>
      </span>
      {children}
    </li>
  );
}

function Development({ data }: { data: NonNullable<InternalProject["development"]> }) {
  const check = (ok: boolean) => (ok ? <NvCheckCircleIcon className="text-nv-brand" /> : null);
  return (
    <div className="mt-7 flex flex-col">
      <h4 className="text-[14px] font-medium leading-[22px] text-nv-text-tertiary">개발과 소스코드</h4>
      <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {data.type && (
          <DevTile icon={<NvCodeIcon />} label="개발 유형">
            <span className="mt-3 text-[16px] font-bold leading-6 text-nv-text-primary">{data.type}</span>
          </DevTile>
        )}
        {data.ownership && (
          <DevTile icon={<NvKeyIcon />} label="코드 소유권">
            <span className="mt-3 text-[16px] font-bold leading-6 text-nv-text-primary">{data.ownership}</span>
          </DevTile>
        )}
        {data.source && (
          <DevTile icon={<NvFolderIcon />} label="소스코드 보유">
            <span className="mt-3 flex items-center gap-[6px] text-[16px] font-bold leading-6 text-nv-text-primary">
              {check(data.source.ok)}
              {data.source.value}
            </span>
            {data.source.note && <span className="mt-3 text-[13px] leading-5 text-nv-text-secondary">{data.source.note}</span>}
          </DevTile>
        )}
        {data.submit && (
          <DevTile icon={<NvSendIcon />} label="소스코드 제출">
            <span className="mt-3 flex items-center gap-[6px] text-[16px] font-bold leading-6 text-nv-text-primary">
              {check(data.submit.ok)}
              {data.submit.value}
            </span>
          </DevTile>
        )}
        {data.assets && (
          <DevTile icon={<NvPageIcon />} label="코드 외 산출물">
            <span className="mt-3 text-[16px] font-bold leading-6 text-nv-text-primary">{data.assets}</span>
          </DevTile>
        )}
      </ul>
    </div>
  );
}

export function ProjectSection({ data }: { data: InternalProject }) {
  return (
    <InternalSection id="project" title="프로젝트 정보" tag>
      <div className="rounded-[20px] border border-nv-border bg-white p-10">
        {data.name && <h3 className="text-[26px] font-normal leading-[35px] text-nv-text-primary">{data.name}</h3>}
        {data.task_name && (
          <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-[12px] bg-nv-bg-page px-6 py-4">
            <span className="text-[14px] leading-[22px] text-nv-text-tertiary">과제명</span>
            <span className="text-[15px] leading-6 text-nv-text-primary">{data.task_name}</span>
          </div>
        )}
        {(data.partner || data.owner) && (
          <div className="mt-7 grid gap-4 md:grid-cols-2">
            {data.partner && <Person name={data.partner.name} role="Engagement Partner" />}
            {data.owner && <Person name={data.owner.title ? `${data.owner.name} ${data.owner.title}` : data.owner.name} role="담당자" />}
          </div>
        )}
        {data.development && <Development data={data.development} />}
        {data.others.length > 0 && (
          <div className="mt-7 border-t border-nv-border pt-7">
            <h4 className="text-[17px] font-medium leading-[27px] text-nv-text-primary">이 프로젝트의 다른 Agent {data.others_total}</h4>
            <ul className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.others.map((o) => (
                <li key={o.id} className="flex">
                  <Link to={`/agents/${o.id}`} className="flex w-full flex-col rounded-[12px] border border-nv-border bg-white px-[21px] pb-5 pt-[20px] hover:border-nv-border-strong">
                    <PictoTile size={56} />
                    <span className="mt-3 line-clamp-2 text-[15px] font-medium leading-[22px] text-nv-text-primary">{o.name}</span>
                    {o.summary && <span className="mt-3 line-clamp-3 text-[13px] leading-5 text-nv-text-secondary">{o.summary}</span>}
                    {o.tags.length > 0 && (
                      <span className="mt-3 flex flex-wrap gap-[6px]">
                        <span className="inline-flex h-[23px] items-center rounded-[8px] bg-nv-bg-page px-2 text-[11px] leading-[17px] text-nv-text-secondary">{o.tags.join(", ")}</span>
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </InternalSection>
  );
}

// ── 유사 에이전트 비교 ──
export function ComparisonSection({ data }: { data: InternalComparison }) {
  const n = data.agents.length;
  const template = `150px repeat(${n}, minmax(0, 1fr))`;
  return (
    <InternalSection id="comparison" title="유사 에이전트 비교" aside="같은 분류의 PwC 내부 Asset과 외부 에이전트를 함께 추천">
      <div className="overflow-x-auto">
        <div className="grid min-w-[760px]" style={{ gridTemplateColumns: template }}>
          <div />
          {data.agents.map((a) => (
            <div key={a.id} className={cn("flex min-w-0 flex-col p-5", a.is_current && "rounded-t-[16px] bg-nv-bg-page")}>
              {a.detail_type === "internal" ? <PictoTile size={48} className="rounded-[11px]" /> : <SupplierLogo name={a.org ?? a.name} logoUrl={a.logo_url} size={48} round={false} />}
              <span className={cn("mt-2 inline-flex h-6 items-center self-start rounded-[8px] px-[10px] text-[12px] leading-[18px]", a.is_current ? "bg-nv-surface-inverse text-white" : "bg-nv-bg-page text-nv-text-secondary")}>
                {a.is_current ? "현재 보는 제품" : "유사"}
              </span>
              <Link to={`/agents/${a.id}`} className="mt-2 break-keep text-[17px] font-medium leading-6 text-nv-text-primary hover:underline">
                {a.name}
              </Link>
              {a.org && <span className="mt-2 text-[13px] leading-[21px] text-nv-text-secondary">{a.org}</span>}
            </div>
          ))}
          {data.rows.map((row, r) => (
            <div key={row.label} className="contents">
              <div className={cn("border-t border-nv-border px-0 py-[21px] text-[14px] leading-[22px] text-nv-text-tertiary", r === data.rows.length - 1 && "border-b")}>{row.label}</div>
              {row.values.map((v, i) => (
                <div
                  key={i}
                  className={cn("flex min-w-0 flex-wrap content-start items-start gap-2 border-t border-nv-border px-5 pb-5 pt-[21px] text-[15px] leading-6 text-nv-text-primary", data.agents[i].is_current && "bg-nv-bg-page", r === data.rows.length - 1 && "border-b")}
                >
                  {row.tags[i] && <span className="inline-flex h-[23px] shrink-0 items-center rounded-[8px] border border-nv-border bg-white px-2 text-[11px] leading-[17px] text-nv-text-tertiary">{row.tags[i]}</span>}
                  {v && <span className="min-w-0 flex-1 break-keep">{v}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </InternalSection>
  );
}
